import { PERMISSIONS } from '../constants/permissions';

/**
 * Get all permissions as a flat array
 * @returns {Array} Array of all permission strings
 */
export const getAllPermissions = () => {
  const permissions = [];

  const extractPermissions = obj => {
    Object.values(obj).forEach(value => {
      if (typeof value === 'string') {
        permissions.push(value);
      } else if (typeof value === 'object') {
        extractPermissions(value);
      }
    });
  };

  extractPermissions(PERMISSIONS);
  return permissions.sort();
};

/**
 * Get permissions grouped by module
 * @returns {Object} Permissions grouped by module
 */
export const getPermissionsByModule = () => {
  const modules = {};

  Object.entries(PERMISSIONS).forEach(([moduleKey, modulePermissions]) => {
    const moduleName = moduleKey.toLowerCase().replace(/_/g, '-');
    modules[moduleName] = {
      name: moduleKey,
      permissions: Object.values(modulePermissions),
    };
  });

  return modules;
};

/**
 * Check if user has specific permission
 * @param {Array} userPermissions - User's permissions array
 * @param {string} requiredPermission - Required permission string
 * @returns {boolean} Whether user has permission
 */
export const hasPermission = (userPermissions, requiredPermission) => {
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }
  return userPermissions.includes(requiredPermission);
};

/**
 * Check if user has any of the specified permissions
 * @param {Array} userPermissions - User's permissions array
 * @param {Array} requiredPermissions - Array of required permissions
 * @returns {boolean} Whether user has any of the permissions
 */
export const hasAnyPermission = (userPermissions, requiredPermissions) => {
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }
  return requiredPermissions.some(permission => userPermissions.includes(permission));
};

/**
 * Check if user has all specified permissions
 * @param {Array} userPermissions - User's permissions array
 * @param {Array} requiredPermissions - Array of required permissions
 * @returns {boolean} Whether user has all permissions
 */
export const hasAllPermissions = (userPermissions, requiredPermissions) => {
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }
  return requiredPermissions.every(permission => userPermissions.includes(permission));
};

/**
 * Get module permissions for a specific module
 * @param {string} moduleName - Module name (e.g., 'MEDICAL_STORE')
 * @returns {Array} Array of permissions for the module
 */
export const getModulePermissions = moduleName => {
  const modulePermissions = PERMISSIONS[moduleName];
  if (!modulePermissions) {
    return [];
  }
  return Object.values(modulePermissions);
};

/**
 * Filter permissions by action type
 * @param {Array} permissions - Array of permissions
 * @param {string} action - Action type (view, create, update, delete, etc.)
 * @returns {Array} Filtered permissions
 */
export const filterPermissionsByAction = (permissions, action) => {
  return permissions.filter(permission => permission.endsWith(`.${action}`));
};

/**
 * Get permission display name
 * @param {string} permission - Permission string
 * @returns {string} Human readable permission name
 */
export const getPermissionDisplayName = permission => {
  return permission
    .split('.')
    .map(part => part.replace(/-/g, ' '))
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' - ');
};

/**
 * Curried function to check permissions against a user object.
 * Compatible with previous Permission.js implementation.
 * @param {Object} userDetails - The user details object containing roles and permissions
 * @returns {Function} A function that takes a permission string and returns boolean
 */
export const matchPermissions = userDetails => permissionString => {
  if (
    !userDetails ||
    !userDetails?.[0] ||
    !userDetails?.[0].role ||
    !userDetails?.[0].role?.permission
  ) {
    return false;
  }

  return userDetails?.[0]?.role?.permission.some(permission => permission === permissionString);
};
